import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { ScriptLoaderModule } from 'ngx-script-loader';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './components/app.component';
import { LoginComponent } from './components/login/login.component';
import { StreetViewModalComponent } from './components/modals/street-view-modal/street-view-modal.component';
import { RegisterComponent } from './components/register/register.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CreateCredentialsComponent } from './components/create-credentials/create-credentials.component';
import { AccountInfoComponent } from './components/account-info/account-info.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { UserComponent } from './components/user/user.component';
import { MapComponent } from './components/map/map.component';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { ReportsComponent } from './components/reports/reports.component';
import { MapReportComponent } from './components/map-report/map-report.component';
import { NewReportComponent } from './components/new-report/new-report.component';
import { TripComponent } from './components/trip/trip.component';
import { TripUploaderComponent } from './components/modals/trip-uploader/trip-uploader.component';
import { TripInfoComponent } from './components/modals/trip-info/trip-info.component';
import { TripDescribeComponent } from './components/modals/trip-describe/trip-describe.component';
import { ConfirmSentComponent } from './components/modals/confirm-sent/confirm-sent.component';
import { ListUserComponent } from './components/management/list-user/list-user.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AddUserComponent } from './components/modals/add-user/add-user.component';
import { FakeAddUserComponent } from './components/modals/fake-add-user/fake-add-user.component';
import { ListProjectComponent } from './components/management/list-project/list-project.component';
import { AddProjectComponent } from './components/modals/add-project/add-project.component';
import { SelectsProjectsComponent } from './components/modals/selects-projects/selects-projects.component';
import { AnalysisReportComponent } from './components/management/analysis-report/analysis-report.component';
import { ChartsModule } from 'ng2-charts';
import { DataService } from './services/data.service';
import { PreviousRouteService } from './services/previous-router.service';
import { ManageMapComponent } from './components/management/manage-map/manage-map.component';
import { AngularFontAwesomeModule } from 'angular-font-awesome';
import { ReportComponent } from './components/management/report/report.component';
import { TripAnalysisComponent } from './components/management/trip-analysis/trip-analysis.component';
import { TripManagementComponent } from './components/management/trip/trip-management.component';
import { ChartUiService } from './services/chart.ui.service';
import { YesnoPipe } from './pipes/yesno.pipe';
import { LoginService } from './services/login.service';
import { AppConfig } from './app.config';
import { UserService } from './services/user.service';
import { MainGuard } from './guards/main.guard';
import { ErrorModalComponent } from './components/modals/error-modal/error-modal.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { InfoModalComponent } from './components/modals/info-modal/info-modal.component';
import { JwtInterceptor } from './services/jwt.interceptor';
import { ChooseProjectComponent } from './components/choose-project/choose-project.component';
import { BackdropComponent } from './components/backdrop/backdrop.component';
import { LeafletDrawModule } from '@asymmetrik/ngx-leaflet-draw';
import { TransportPipe } from './pipes/transport.pipe';
import { CategoryPipe } from './pipes/category.pipe';
import { SourceDevicePipe } from './pipes/source-device.pipe';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { SelectImagesComponent } from './components/select-images/select-images.component';
import { ReasonComponent } from './components/modals/reason/reason.component';
import { TranslateProvider } from './services/providers/translate-provider';
import { ForgotPwdComponent } from './components/forgot-pwd/forgot-pwd.component';
import { UpdatePasswordComponent } from './components/modals/update-password/update-password.component';
import { ImgPipe } from './pipes/img.pipe';
import { DictPipe } from './pipes/dict.pipe';
import { RespondTicketComponent } from './components/modals/respond-ticket/respond-ticket.component';
import { EditTripComponent } from './components/management/edit-trip/edit-trip.component';
import { PublicMapComponent } from './components/public-map/public-map.component';
import { ProjectService } from './services/project.service';
import { MergeReportComponent } from './components/modals/merge-report/merge-report.component';
import { SidebarModule } from 'ng-sidebar';
import { MatTreeModule, MatIconModule, MatButtonModule, MatDatepickerModule, MatNativeDateModule, MatInputModule } from '@angular/material';
import { SocialLoginComponent } from './components/social-login/social-login.component';
import { TicketImagesModalComponent } from './components/modals/ticket-images/ticket-images-modal.component';
import { TranslationLoaderService } from './services/translate-loader.service';
import { NewLayerComponent } from './components/modals/new-layer/new-layer.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { AccountVerificationComponent } from './components/account-verification/account-verification.component';
import { ResendVerification } from './components/modals/resend-verification/resend-verification.component';
import { GetStartComponent } from './components/get-started/get-started.component';
import { NgxMaskModule, IConfig } from 'ngx-mask';
import { AppToastsComponent } from './components/toasts/toasts.component';
import { LanguageDropdownComponent } from './components/language-dropdown/language-dropdown.component';
import { LogoComponent } from './components/logo/logo.component';

const maskConfig: Partial<IConfig> = {
  validation: false,
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateProvider(http);
}

@NgModule({
  declarations: [
    AppComponent,
    AppToastsComponent,
    LoginComponent,
    RegisterComponent,
    CreateCredentialsComponent,
    AccountInfoComponent,
    AccountVerificationComponent,
    UserComponent,
    MapComponent,
    PublicMapComponent,
    GetStartComponent,
    ReportsComponent,
    MapReportComponent,
    NewReportComponent,
    TripComponent,
    TripUploaderComponent,
    TripInfoComponent,
    TripDescribeComponent,
    ConfirmSentComponent,
    ListUserComponent,
    AddUserComponent,
    FakeAddUserComponent,
    ListProjectComponent,
    AddProjectComponent,
    ResendVerification,
    SelectsProjectsComponent,
    AnalysisReportComponent,
    MergeReportComponent,
    NewLayerComponent,
    ManageMapComponent,
    ReportComponent,
    TripAnalysisComponent,
    TripManagementComponent,
    YesnoPipe,
    ErrorModalComponent,
    InfoModalComponent,
    StreetViewModalComponent,
    TicketImagesModalComponent,
    ChooseProjectComponent,
    BackdropComponent,
    TransportPipe,
    CategoryPipe,
    SourceDevicePipe,
    FileUploadComponent,
    SelectImagesComponent,
    ReasonComponent,
    ForgotPwdComponent,
    ImgPipe,
    ForgotPwdComponent,
    UpdatePasswordComponent,
    DictPipe,
    RespondTicketComponent,
    EditTripComponent,
    SocialLoginComponent,
    LanguageDropdownComponent,
    LogoComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NgbModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useFactory: HttpLoaderFactory, deps: [HttpClient] },
    }),
    LeafletModule.forRoot(),
    LeafletDrawModule.forRoot(),
    ScriptLoaderModule,
    FormsModule,
    ChartsModule,
    AngularFontAwesomeModule,
    NgSelectModule,
    ReactiveFormsModule,
    SidebarModule.forRoot(),
    MatTreeModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ColorPickerModule,
    NgxMaskModule.forRoot(maskConfig),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    DataService,
    PreviousRouteService,
    ChartUiService,
    LoginService,
    AppConfig,
    UserService,
    TranslationLoaderService,
    ProjectService,
    MainGuard,
    CategoryPipe,
    TransportPipe
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    TripUploaderComponent,
    TripInfoComponent,
    TripDescribeComponent,
    ConfirmSentComponent,
    AddUserComponent,
    FakeAddUserComponent,
    AddProjectComponent,
    ResendVerification,
    SelectsProjectsComponent,
    ErrorModalComponent,
    InfoModalComponent,
    StreetViewModalComponent,
    TicketImagesModalComponent,
    MergeReportComponent,
    NewLayerComponent,
    ChooseProjectComponent,
    BackdropComponent,
    ReasonComponent,
    UpdatePasswordComponent,
    RespondTicketComponent
  ]
})
export class AppModule {

}
